import React, { createContext, useContext, useRef, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    UfoCustomTimingKey,
    ufoEventOnboardingInviteUsers
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";

import { useCloudStatus } from "../../../../../../hooks";
import { ANALYTICS_PREFIX } from "../../../../common/constants";

import { Fetched } from "./types";
import {
    ProjectRoleIds,
    useJfind1663FetchProjectRoleIds
} from "./use-jfind-1663-fetch-project-role-ids";
import { parseJiraEditionFromLicenseInformation } from "./utils";

const PROJECT_CREATE_TASK_POLLING_TIMEOUT = 17000;

interface CreateProjectTaskPollingContext {
    projectId: Fetched<string>;
    projectRoleIds: Fetched<ProjectRoleIds>;
    jiraEdition: Fetched<string>;
    pollTaskByTaskIdQueryParam: () => Promise<boolean>;
}

const DEFAULT = {
    projectId: undefined,
    projectRoleIds: undefined,
    jiraEdition: undefined,
    pollTaskByTaskIdQueryParam: () => Promise.resolve(false)
} satisfies CreateProjectTaskPollingContext;

const CreateProjectTaskPollingContext =
    createContext<CreateProjectTaskPollingContext>(DEFAULT);

export const Jfind1663CreateProjectTaskPollingProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const stargateClient = useBxpWebStargateClient();
    const { cloudId } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { fetchRoles } = useJfind1663FetchProjectRoleIds();

    const hasPolledRef = useRef(false);

    const [projectId, setProjectId] = useState<Fetched<string>>(null);

    const [projectRoleIds, setProjectRoleIds] =
        useState<Fetched<ProjectRoleIds>>(null);

    const [jiraEdition, setJiraEdition] = useState<Fetched<string>>(null);

    const pollTaskByTaskIdQueryParam = async (): Promise<boolean> => {
        try {
            const taskId = new URL(window.location.href).searchParams.get(
                "taskId"
            );

            if (hasPolledRef.current || !taskId) {
                // prevent double polling or polling when no taskId is present
                return false;
            }

            hasPolledRef.current = true;

            ufoEventOnboardingInviteUsers.startSubstep(
                UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
            );

            const { status, result } =
                await stargateClient.pollTaskUntilComplete(
                    taskId,
                    cloudId,
                    PROJECT_CREATE_TASK_POLLING_TIMEOUT
                );

            if (status === "COMPLETE") {
                const projectId = result?.projectId;

                setProjectId(projectId);

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
                );
                ufoEventOnboardingInviteUsers.startSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );

                const roleIds = await fetchRoles(cloudId, projectId);
                setProjectRoleIds(roleIds);

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );

                if (roleIds.admin) {
                    // elevating invited user to Admin for Free Edtition requires Admin role is available

                    ufoEventOnboardingInviteUsers.startSubstep(
                        UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE
                    );

                    const licenseInformation =
                        await stargateClient.getLicenceInformationStatus({
                            cloudId
                        });
                    const edition =
                        parseJiraEditionFromLicenseInformation(
                            licenseInformation
                        );
                    setJiraEdition(edition);

                    ufoEventOnboardingInviteUsers.endSubstep(
                        UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE
                    );
                }
            } else {
                setProjectId(null);

                await Promise.resolve(
                    createAnalyticsEvent({
                        action: "failed",
                        actionSubject: "polling",
                        actionSubjectId: `${ANALYTICS_PREFIX}InviteCreateProjectTaskPolling`,
                        eventType: "track",
                        attributes: {
                            taskStatus: status,
                            taskId
                        }
                    }).fire()
                );
            }
        } catch (error) {
            setProjectId(null);
        }

        // once we have resolved our polling, resolve the promise to unblock submission
        return true;
    };

    return (
        <CreateProjectTaskPollingContext.Provider
            value={{
                projectId,
                projectRoleIds,
                jiraEdition,
                pollTaskByTaskIdQueryParam
            }}
        >
            {children}
        </CreateProjectTaskPollingContext.Provider>
    );
};

export const useJfind1663TaskPolling = (): CreateProjectTaskPollingContext => {
    const context = useContext(CreateProjectTaskPollingContext);
    if (!context) {
        // Do not throw error to enable harmless usage when unenrolled
        return DEFAULT;
    }

    return context;
};
